// src/pages/team/TeamResourcesTab.tsx
// Shared resources of the team: full-page view of the team entity's linked
// resources. Reuses the same components as the global right rail
// (ResourceList / ResourceCommandMenu / ResourceManagePanel) so linking,
// unlinking and management behave identically everywhere.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { entityStore } from '@/stores/EntityStore';
import { groupResourcesByRelation, entityToRoute } from '@/features/resources/logic';
import { unlinkResources } from '@/features/resources/api';
import { ResourceManagePanel } from '@/features/resources/ResourceManagePanel';
import type { ResourceCardData } from '@/features/resources/types';
import { ResourceList } from '@/components/ResourceList';
import { ResourceCommandMenu } from '@/components/ResourceCommandMenu';
import type { Resource } from '@core/types/resource';

interface TeamResourcesTabProps {
  teamId: string;
  teamUrn: string;
  teamName: string;
}

export const TeamResourcesTab = observer(({ teamId, teamUrn, teamName }: TeamResourcesTabProps) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // The workspace page already sets entityStore context for the team; this
  // effect covers deep links straight into ?tab=resources.
  useEffect(() => {
    if (entityStore.currentEntity?.id !== teamId) {
      entityStore.loadEntityData({ urn: teamUrn, type: 'team', id: teamId, title: teamName });
    }
  }, [teamId, teamUrn, teamName]);

  const handleNavigate = (data: ResourceCardData) => {
    const route = entityToRoute({ type_id: data.typeId, id: data.id });
    if (route) navigate(route);
  };

  const handleUnlink = (resourceId: string) => {
    const current = entityStore.currentEntity;
    const resource = entityStore.resources.find((r) => r.entity?.id === resourceId);
    entityStore.removeResourceOptimistic(resourceId);
    if (current && resource?.urn) {
      unlinkResources(
        { urn: current.urn } as never,
        { urn: resource.urn } as never,
        resource.relation_type,
      ).catch(() => {
        entityStore.addResourceOptimistic(resource);
      });
    }
  };

  const handleLinked = (resource: Resource) => {
    entityStore.addResourceOptimistic(resource);
  };

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-muted-foreground">{t('team.resources.hint')}</p>

      <ResourceList
        groups={groupResourcesByRelation(entityStore.resources)}
        isLoading={entityStore.isLoadingResources}
        onUnlink={handleUnlink}
        onNavigate={handleNavigate}
        onAddClick={() => setMenuOpen(true)}
        onManageClick={() => setManageOpen(true)}
      />

      <ResourceCommandMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        entityUrn={teamUrn}
        entityType="team"
        onLinked={handleLinked}
      />

      {manageOpen && entityStore.currentEntity && (
        <ResourceManagePanel
          entity={entityStore.currentEntity}
          onClose={() => setManageOpen(false)}
          onChanged={() => entityStore.loadResources(entityStore.currentEntity!)}
        />
      )}
    </div>
  );
});

export default TeamResourcesTab;
